import React from 'react';
import { Check, Minus } from 'lucide-react';
import { EXPORT_COLUMN_GROUPS } from '@/types/import';
import { cn } from '@/lib/utils';

interface ColumnSelectorProps {
  selectedColumns: string[];
  onChange: (columns: string[]) => void;
}

export const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  selectedColumns,
  onChange,
}) => {
  const allColumns = Object.values(EXPORT_COLUMN_GROUPS)
    .flatMap(group => group.columns)
    .map(col => col.value);

  const isAllSelected = allColumns.every(col => selectedColumns.includes(col));
  const isSomeSelected = selectedColumns.length > 0 && !isAllSelected;

  const handleSelectAll = () => {
    if (isAllSelected) {
      onChange([]);
    } else {
      onChange(allColumns);
    }
  };

  const handleGroupToggle = (groupKey: string) => {
    const group = EXPORT_COLUMN_GROUPS[groupKey as keyof typeof EXPORT_COLUMN_GROUPS];
    const groupColumns = group.columns.map(col => col.value);
    const allGroupSelected = groupColumns.every(col => selectedColumns.includes(col));

    if (allGroupSelected) {
      onChange(selectedColumns.filter(col => !groupColumns.includes(col)));
    } else {
      const newColumns = new Set([...selectedColumns, ...groupColumns]);
      onChange(Array.from(newColumns));
    }
  };

  const handleColumnToggle = (column: string) => {
    if (selectedColumns.includes(column)) {
      onChange(selectedColumns.filter(col => col !== column));
    } else {
      onChange([...selectedColumns, column]);
    }
  };

  const isGroupSelected = (groupKey: string): 'all' | 'some' | 'none' => {
    const group = EXPORT_COLUMN_GROUPS[groupKey as keyof typeof EXPORT_COLUMN_GROUPS];
    const groupColumns = group.columns.map(col => col.value);
    const selectedInGroup = groupColumns.filter(col => selectedColumns.includes(col));

    if (selectedInGroup.length === groupColumns.length) return 'all';
    if (selectedInGroup.length > 0) return 'some';
    return 'none';
  };

  return (
    <div className="space-y-4">
      {/* Select all */}
      <div className="flex items-center justify-between pb-3 border-b">
        <label className="flex items-center space-x-3 cursor-pointer">
          <div
            onClick={handleSelectAll}
            className={cn(
              'w-5 h-5 rounded border flex items-center justify-center cursor-pointer transition-colors',
              isAllSelected
                ? 'bg-blue-600 border-blue-600'
                : isSomeSelected
                ? 'bg-blue-200 border-blue-400'
                : 'border-gray-300 hover:border-gray-400'
            )}
          >
            {isAllSelected && <Check className="w-3 h-3 text-white" />}
            {isSomeSelected && <Minus className="w-3 h-3 text-blue-600" />}
          </div>
          <span className="text-sm font-medium text-gray-900">
            {isAllSelected ? 'Deselect all' : 'Select all columns'}
          </span>
        </label>
        <span className="text-sm text-gray-500">
          {selectedColumns.length} of {allColumns.length} selected
        </span>
      </div>

      {/* Column groups */}
      <div className="space-y-4">
        {Object.entries(EXPORT_COLUMN_GROUPS).map(([key, group]) => {
          const groupSelection = isGroupSelected(key);

          return (
            <div key={key} className="border rounded-lg">
              {/* Group header */}
              <div
                onClick={() => handleGroupToggle(key)}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-t-lg cursor-pointer hover:bg-gray-100"
              >
                <label className="flex items-center space-x-3 cursor-pointer">
                  <div
                    className={cn(
                      'w-5 h-5 rounded border flex items-center justify-center transition-colors',
                      groupSelection === 'all'
                        ? 'bg-blue-600 border-blue-600'
                        : groupSelection === 'some'
                        ? 'bg-blue-200 border-blue-400'
                        : 'border-gray-300'
                    )}
                  >
                    {groupSelection === 'all' && <Check className="w-3 h-3 text-white" />}
                    {groupSelection === 'some' && <Minus className="w-3 h-3 text-blue-600" />}
                  </div>
                  <span className="text-sm font-medium text-gray-900">{group.label}</span>
                </label>
                <span className="text-xs text-gray-500">
                  {group.columns.filter(col => selectedColumns.includes(col.value)).length} / {group.columns.length}
                </span>
              </div>

              {/* Group columns */}
              <div className="p-3 space-y-2">
                {group.columns.map((column) => {
                  const isSelected = selectedColumns.includes(column.value);

                  return (
                    <label
                      key={column.value}
                      className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-1 rounded"
                    >
                      <div
                        onClick={(e) => {
                          e.preventDefault();
                          handleColumnToggle(column.value);
                        }}
                        className={cn(
                          'w-4 h-4 rounded border flex items-center justify-center transition-colors',
                          isSelected
                            ? 'bg-blue-600 border-blue-600'
                            : 'border-gray-300 hover:border-gray-400'
                        )}
                      >
                        {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                      </div>
                      <span className="text-sm text-gray-700">{column.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
